import { useEffect } from 'react';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotificationWatcher } from '../hooks/useNotificationWatcher';
import { Notification } from '../types/notification';
import { PRIORITY_COLOR, getNotificationMeta } from '../config/notificationMeta';
import { useOpenNotification } from '../hooks/useOpenNotification';

const AUTO_DISMISS_MS = 8_000;

/** Real-time pop-up notification (§2) — a stack of non-blocking cards in
 * the top-right corner, never a blocking browser alert. Mounted once in
 * AppLayout, gated on the same notifications:view permission the bell
 * already uses. */
const ToastCard = ({ notification, onDismiss }: { notification: Notification; onDismiss: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = getNotificationMeta(notification.type);
  const openNotification = useOpenNotification();

  const handleView = () => {
    void openNotification(notification);
    onDismiss();
  };

  return (
    <Paper
      elevation={6}
      sx={{
        width: 360,
        p: 2,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: PRIORITY_COLOR[meta.priority],
        cursor: 'pointer',
      }}
      onClick={handleView}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color: PRIORITY_COLOR[meta.priority], display: 'flex', pt: 0.25 }}>
          <meta.icon fontSize="small" />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {notification.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {notification.message}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            just now
          </Typography>
          <Typography variant="button" color="primary" sx={{ display: 'inline-block', mt: 1, fontSize: '0.75rem' }}>
            View →
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="dismiss notification"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export const NotificationToastHost = () => {
  const { toasts, dismiss } = useNotificationWatcher();

  if (toasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 80,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
    >
      {toasts.slice(0, 4).map((notification) => (
        <ToastCard key={notification.id} notification={notification} onDismiss={() => dismiss(notification.id)} />
      ))}
    </Box>
  );
};
